import { ComponentDataDestService } from '../component-data-dest.service';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';
import { environment } from '../../../environment/environment';
import { EventParticipantDTO } from '../../../dto/event/event-participant.dto';
import { Observable, tap, catchError, shareReplay } from 'rxjs';
import { Router } from '@angular/router';

function extractUserIds(data: any): string[] {
  if (!data) {
    return [];
  }

  if (Array.isArray(data.dynamic_table_users)) {
    return data.dynamic_table_users.filter((id: unknown) => typeof id === 'string');
  }

  if (data.dualPanelSelector?.dualPanelSelector && Array.isArray(data.dualPanelSelector.dualPanelSelector)) {
    return data.dualPanelSelector.dualPanelSelector;
  }
  if (data.dualPanelSelector && Array.isArray(data.dualPanelSelector)) {
    return data.dualPanelSelector;
  }
  if (data.event_users && Array.isArray(data.event_users)) {
    return data.event_users;
  }
  if (data.event_users && typeof data.event_users === 'object') {
    if (data.event_users.event_users && Array.isArray(data.event_users.event_users)) {
      return data.event_users.event_users;
    }
    const fromValues: string[] = [];
    Object.values(data.event_users).forEach((value) => {
      if (typeof value === 'string') {
        fromValues.push(value);
      }
    });
    return fromValues;
  }

  for (const key of Object.keys(data)) {
    if (key.includes('users') && Array.isArray(data[key])) {
      return data[key];
    }
  }

  return [];
}

function resolveSendNotifications(data: any): boolean {
  if (!data || !Object.prototype.hasOwnProperty.call(data, 'send_notifications')) {
    return true;
  }
  const v = data.send_notifications;
  return v === true || v === 'true' || v === 1 || v === '1';
}

function resolveEnrollmentRoleAndStatus(
  data: any
): { role: 'attendee' | 'instructor'; status: 'pending' | 'waitlist' } {
  const raw = data?.enrollment_status;
  if (raw === 'instructor') {
    return { role: 'instructor', status: 'pending' };
  }
  if (raw === 'waitlist' || raw === 'waitlisted') {
    return { role: 'attendee', status: 'waitlist' };
  }
  return { role: 'attendee', status: 'pending' };
}

export const update_event_users = (
  service: ComponentDataDestService,
  componentDataSourceService: ComponentDataSourceService,
  data: any
) => {
  const refreshEventParticipationDataSources = (request$: Observable<any>) => {
    const sharedRequest$ = request$.pipe(shareReplay(1));

    componentDataSourceService.updateStoreData(sharedRequest$, 'event_participants').subscribe({
      error: (error) => {
        console.error('Update event users participants subscription error:', error);
      }
    });

    componentDataSourceService.updateStoreData(sharedRequest$, 'event_instructors').subscribe({
      error: (error) => {
        console.error('Update event users instructors refresh error:', error);
      }
    });

    componentDataSourceService.updateStoreData(sharedRequest$, 'event_waitlist').subscribe({
      error: (error) => {
        console.error('Update event users waitlist refresh error:', error);
      }
    });

    componentDataSourceService.updateStoreData(sharedRequest$, 'event_cancellations').subscribe({
      error: (error) => {
        console.error('Update event users cancellations refresh error:', error);
      }
    });
  };

  const router = service.injector.get(Router);
  const pathSegments = router.url.split('/');
  const eventGuid = pathSegments.find((segment: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
  );

  if (!eventGuid) {
    console.error('No valid event GUID found in URL');
    throw new Error('No valid event GUID found in URL');
  }

  const userIds = extractUserIds(data);
  if (userIds.length === 0) {
    console.warn('No user IDs found in form data');
  }

  const sendNotifications = resolveSendNotifications(data);
  const { role, status } = resolveEnrollmentRoleAndStatus(data);

  const participants: EventParticipantDTO[] = userIds.map((userId) => ({
    user: {
      uuid: userId,
      first_name: '',
      last_name: '',
      user: null,
      roles: null,
      organizations_id: null,
      locations: null,
      status: null,
    },
    role,
    status,
  }));

  const url = `${environment.apiUrl}/events/${eventGuid}/participants`;
  const payload = {
    participants,
    send_notifications: sendNotifications,
  };

  const request$ = service.http.post<EventParticipantDTO[]>(url, payload).pipe(
    catchError((error) => {
      console.error('Error in update event users:', error);
      if (error.error?.message) {
        console.error('Server error message:', error.error.message);
      }
      throw error;
    })
  );

  refreshEventParticipationDataSources(request$);
};
